import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	SURFACE_DEFECT_INITIAL_VALUES,
	SurfaceDefect,
} from "@/shared/models/Inspection";
import {
	deleteSurfaceDefect,
	upsertSurfaceDefect,
} from "@/shared/repositories/SurfaceDefectRepository";
import ColorPicker from "@/pages/configuration/delay-categories/components/ColorPicker/ColorPicker";
import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

export const surfaceDefectColors: string[] = [
	"#2a78d6", // blue
	"#eb6834", // orange
	"#1baf7a", // green
	"#eda100", // amber
	"#e87ba4", // pink
	"#8b5cf6", // violet
	"#0fa3b1", // cyan
	"#d94343", // red
	"#6b8e23", // olive
	"#a0632b", // brown
	"#4f5bd5", // indigo
	"#c0479a", // magenta
	"#14746f", // dark teal
	"#f28f79", // salmon
	"#57b8ff", // sky blue
	"#80b918", // lime
];

type Props = {
	surfaceDefect: SurfaceDefect | null;
	modal: "create" | "update" | "delete" | "";
	onHide: () => void;
};

export const UpsertDeleteModal = ({ surfaceDefect, modal, onHide }: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");

	const isDelete = modal === "delete";
	const title = modal === "create" ? "CREATE" : isDelete ? "DELETE" : "EDIT";

	const { control, reset, handleSubmit } = useForm<SurfaceDefect>({
		defaultValues: SURFACE_DEFECT_INITIAL_VALUES,
	});

	const upsertMutation = useMutation(upsertSurfaceDefect, {
		meta: {
			successMessage: `The surface defect was ${
				modal === "create" ? "created" : "updated"
			} successfully`,
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["surface-defects"] });
			onHide();
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const deleteMutation = useMutation(deleteSurfaceDefect, {
		meta: { successMessage: "The surface defect was deleted successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["surface-defects"] });
			onHide();
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const onSubmit: SubmitHandler<SurfaceDefect> = (data) => {
		if (isDelete) deleteMutation.mutate(data.SurfaceDefectId);
		else upsertMutation.mutate(data);
	};

	useEffect(() => {
		if (modal === "") return;
		reset(surfaceDefect || SURFACE_DEFECT_INITIAL_VALUES);
	}, [modal, surfaceDefect]);

	return (
		<>
			{modal !== "" && (
				<HelmetDexteel title={`${title} Surface Defect - Surface Defects`} />
			)}
			<MesfModal
				id="modal-surface-defect"
				title={`${title} SURFACE DEFECT`}
				open={modal !== ""}
				handleClose={onHide}
				maxWidth="sm"
			>
				<form
					data-test-id="surface-defect-form"
					onSubmit={handleSubmit(onSubmit)}
				>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="SurfaceDefectName"
									control={control}
									rules={{ required: "Name is required" }}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											required
											label="Name"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
											slotProps={{
												htmlInput: {
													"data-test-id": "surface-defect-name-input",
												},
											}}
											value={field.value}
											disabled={isDelete}
											onChange={field.onChange}
											error={!!fieldError}
											helperText={fieldError?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }} style={{ marginTop: 10 }}>
								<Controller
									name="Color"
									control={control}
									render={({ field }) => (
										<ColorPicker
											onChange={(_: string, color: string) =>
												field.onChange(color)
											}
											color={field.value ?? ""}
											colors={surfaceDefectColors}
											disabled={isDelete}
											label="Color"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={
								upsertMutation.isLoading || deleteMutation.isLoading
							}
							onHide={onHide}
							buttonLabel={isDelete ? "Delete" : "Save"}
							buttonColor={isDelete ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
